/**
 * Type-to-filter enhancement for long <select> elements.
 *
 * Progressive enhancement on purpose: the <select> stays in the DOM and stays
 * the value, so anything reading `.value`, `[data-fmap]`, or listening for
 * `change` keeps working untouched, and a render that rebuilds the markup gets
 * the plain control back until it is enhanced again. The combobox is only a way
 * to drive it. That matters here because an agency with sixty custom fields, or
 * a location with a hundred workflows, cannot find anything in a native list.
 */

/**
 * Short lists are worse as comboboxes than as selects — an extra click and a
 * text field to skim four options — so enhancement starts where scanning stops
 * being possible.
 */
const MIN_OPTIONS = 8;

interface Row {
  value: string;
  label: string;
  /** Index in the <select>, so selecting writes back to the right option. */
  index: number;
}

/**
 * Enhances every <select> under `root` that is long enough to warrant it, and is
 * not enhanced already. Safe to call after every render.
 */
export function enhanceSelects(root: HTMLElement, minOptions = MIN_OPTIONS): void {
  root.querySelectorAll<HTMLSelectElement>('select').forEach((select) => {
    if (select.dataset.combo === 'on' || select.options.length < minOptions) return;
    enhance(select);
  });
}

function enhance(select: HTMLSelectElement): void {
  select.dataset.combo = 'on';

  const rows: Row[] = Array.from(select.options).map((o, index) => ({
    value: o.value,
    label: o.textContent ?? '',
    index,
  }));

  const wrap = document.createElement('div');
  wrap.className = 'combo';
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'combo-input';
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-autocomplete', 'list');
  const list = document.createElement('div');
  list.className = 'combo-list';
  list.setAttribute('role', 'listbox');
  list.hidden = true;
  // Tie the two together for screen readers. The id is derived from the select's
  // so it stays stable across re-renders and unique per control.
  const listId = 'combo-list-' + (select.id || Math.abs(hash(select.name || rows.length + rows[0]?.label)).toString(36));
  list.id = listId;
  input.setAttribute('aria-controls', listId);

  // The select keeps the value but must stop being reachable, or Tab lands on a
  // control the reader cannot see and the combobox is bypassed silently.
  select.hidden = true;
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');
  select.parentNode!.insertBefore(wrap, select);
  wrap.appendChild(input);
  wrap.appendChild(select);
  wrap.appendChild(list);

  /** Index into `rows` of the keyboard-highlighted option, -1 for none. */
  let active = -1;
  let shown: Row[] = rows;

  const labelOf = (value: string) => rows.find((r) => r.value === value)?.label ?? '';
  const resetInput = () => { input.value = labelOf(select.value); };
  resetInput();

  function draw(): void {
    list.textContent = '';
    if (!shown.length) {
      const none = document.createElement('div');
      none.className = 'combo-empty';
      none.textContent = 'No matches';
      list.appendChild(none);
      return;
    }
    shown.forEach((row, i) => {
      const opt = document.createElement('div');
      opt.className = 'combo-opt' + (i === active ? ' active' : '') + (row.value === select.value ? ' chosen' : '');
      opt.setAttribute('role', 'option');
      opt.setAttribute('aria-selected', String(row.value === select.value));
      opt.id = listId + '-o' + row.index;
      opt.textContent = row.label;
      // mousedown, not click: click fires after blur, which has already closed
      // the list and thrown the choice away.
      opt.addEventListener('mousedown', (e) => { e.preventDefault(); choose(row); });
      list.appendChild(opt);
    });
    if (active >= 0) {
      input.setAttribute('aria-activedescendant', listId + '-o' + shown[active].index);
      list.children[active]?.scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  function open(filter = ''): void {
    const q = filter.trim().toLowerCase();
    shown = q ? rows.filter((r) => r.label.toLowerCase().includes(q)) : rows;
    // Land on the current value when opening unfiltered, so Enter is a no-op
    // rather than a silent change to whatever happened to be first.
    active = q ? (shown.length ? 0 : -1) : shown.findIndex((r) => r.value === select.value);
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    draw();
  }

  function close(): void {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  }

  function choose(row: Row): void {
    select.selectedIndex = row.index;
    resetInput();
    close();
    // The panel re-renders on some of these (pipeline drives the stage list), so
    // this has to be a real event and not a direct call.
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  input.addEventListener('focus', () => open());
  input.addEventListener('input', () => open(input.value));
  input.addEventListener('blur', () => {
    // Abandoned typing is not a change: restore the label so the field never
    // shows text that disagrees with the value underneath it.
    resetInput();
    close();
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (list.hidden) { open(); return; }
      if (!shown.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      active = (active + step + shown.length) % shown.length;
      draw();
    } else if (e.key === 'Enter') {
      // Only swallow Enter when it is actually picking something, so the key
      // still reaches the surrounding form otherwise.
      if (!list.hidden && active >= 0 && shown[active]) { e.preventDefault(); choose(shown[active]); }
    } else if (e.key === 'Escape') {
      if (!list.hidden) { e.stopPropagation(); resetInput(); close(); }
    }
  });
}

/** Cheap stable id source for a select with no id of its own. */
function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}
